/* ============================================================
   printerCatalogPickerView — pure view model for the
   «Из каталога» printer picker (PF-3, docs/provider-fleet-spec.md §5.1).

   ProviderPrintersPanel derives every rendered row from this
   module so the picker contract stays node-testable without a
   browser: query/alias search, brand chips, capped result list,
   catalog prefill defaults (Name defaults to the model name),
   and the locked-fields set mirrored from PF-2 (server rejects
   changes to catalog-derived fields while a printer is linked).

   Reference data comes from GET /api/v1/printer-catalog (PF-1);
   the server stamps Technology/Brand/Model/build volume from
   the catalog (PF-2) — the web layer never invents them.
   ============================================================ */

import type { PrinterModelCatalogResponse, PrinterModelEntry, PrinterTechnology } from '../types/provider';

export type { PrinterModelCatalogResponse, PrinterModelEntry };

/**
 * API enum wire format is ordinal numbers (AddControllers default JSON,
 * same drift bambuView.normalizeBambuConnectionStatus defends against):
 * 0 Fdm / 1 Sla / 2 Sls. Strings keep exact-case and lower-case forms;
 * anything else degrades to Fdm so downstream lookups never see undefined.
 */
export function normalizePrinterTechnology(
  value: PrinterTechnology | number | null | undefined,
): PrinterTechnology {
  if (typeof value === 'string') {
    const s = value.toLowerCase();
    if (s === 'fdm') return 'Fdm';
    if (s === 'sla') return 'Sla';
    if (s === 'sls') return 'Sls';
  } else if (value === 0 || value === 1 || value === 2) {
    return (['Fdm', 'Sla', 'Sls'] as const)[value];
  }
  return 'Fdm';
}

/** Form mode toggle (§5.1): «Из каталога» by default, «Свой принтер» = today's form. */
export type PrinterFormMode = 'catalog' | 'custom';

export const PRINTER_TECHNOLOGY_LABELS: Record<PrinterTechnology, string> = {
  Fdm: 'FDM',
  Sla: 'SLA / DLP',
  Sls: 'SLS / MJF',
};

/**
 * Fields the server stamps from the catalog while a printer is linked
 * (PF-2 e6d6ab7d): locked in UI, changes rejected by the update endpoint.
 * Name / HourlyRateRub / IsActive stay editable even when linked.
 */
export const CATALOG_LOCKED_FIELDS = ['brand', 'model', 'technology', 'buildVolume'] as const;

/** Max entries rendered in the picker list — keeps the cabinet form scannable. */
export const MAX_PICKER_RESULTS = 12;

/** Case-insensitive search token: trim + lowercase (aliases are seeded lowercase). */
export function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

/** True when brand, model, slug or any seeded alias contains the query. */
export function entryMatches(entry: PrinterModelEntry, query: string): boolean {
  const q = normalizeSearch(query);
  if (!q) return true;
  const haystack = [
    entry.brand,
    entry.model,
    entry.slug,
    ...(entry.aliases ?? []),
  ].map((v) => (v ?? '').toLowerCase());
  return haystack.some((v) => v.includes(q));
}

export interface PickerFilter {
  query: string;
  /** Selected brand chip, or null for “all brands”. */
  brand: string | null;
}

/** Query + brand chip combined; capped at MAX_PICKER_RESULTS. */
export function filterCatalogEntries(
  entries: PrinterModelEntry[],
  filter: PickerFilter,
): PrinterModelEntry[] {
  const brand = filter.brand?.trim().toLowerCase() ?? '';
  return entries
    .filter((e) => entryMatches(e, filter.query))
    .filter((e) => !brand || e.brand.toLowerCase() === brand)
    .slice(0, MAX_PICKER_RESULTS);
}

/** Distinct brands for the chip row, most models first, then alphabetical. */
export function distinctBrands(entries: PrinterModelEntry[]): string[] {
  const counts = new Map<string, number>();
  for (const e of entries) {
    counts.set(e.brand, (counts.get(e.brand) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([brand]) => brand);
}

/** «Bambu Lab X1 Carbon · FDM · 256×256×256 мм» — one-line picker/summary copy. */
export function pickerSummary(entry: PrinterModelEntry): string {
  return `${entry.brand} ${entry.model} · ${PRINTER_TECHNOLOGY_LABELS[entry.technology]} · ${formatBuildVolume(entry)}`;
}

/** «256×256×256 мм» — decimals keep their fractional part (Elegoo Mars 4 = 153.36×77.76×175). */
export function formatBuildVolume(entry: Pick<PrinterModelEntry, 'buildVolumeX' | 'buildVolumeY' | 'buildVolumeZ'>): string {
  return `${entry.buildVolumeX}×${entry.buildVolumeY}×${entry.buildVolumeZ} мм`;
}

/**
 * Form values a selected catalog entry prefills (§5.1): the provider enters
 * only Name (defaults to the model name) and the hourly rate; catalog-derived
 * fields are display-only — the server re-stamps them from the slug (PF-2).
 */
export interface CatalogFormDefaults {
  name: string;
  technology: PrinterTechnology;
  brand: string;
  model: string;
  buildVolumeX: number;
  buildVolumeY: number;
  buildVolumeZ: number;
}

export function catalogDefaults(entry: PrinterModelEntry): CatalogFormDefaults {
  return {
    name: entry.model,
    technology: entry.technology,
    brand: entry.brand,
    model: entry.model,
    buildVolumeX: entry.buildVolumeX,
    buildVolumeY: entry.buildVolumeY,
    buildVolumeZ: entry.buildVolumeZ,
  };
}

/** True while the printer row is catalog-linked → derived fields locked in UI. */
export function isCatalogLinked(printer: { catalogSlug?: string | null }): boolean {
  return typeof printer.catalogSlug === 'string' && printer.catalogSlug.length > 0;
}
